import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/apiClient';

type RangeKey = '24h' | '7d' | '30d' | '60d' | '90d' | 'all';
const RANGES: { key: RangeKey; label: string }[] = [
  { key: '24h', label: '24h' },
  { key: '7d',  label: '7d' },
  { key: '30d', label: '30d' },
  { key: '60d', label: '60d' },
  { key: '90d', label: '90d' },
  { key: 'all', label: 'All' },
];

interface TriageRow {
  severity: string;
  triageCount: number;
  closeCount: number;
  mtttSeconds: number | null;
  mttcSeconds: number | null;
  mtttFormatted: string;
  mttcFormatted: string;
  ttaPolicyMinutes: number;
  ttrPolicyMinutes: number;
  status: 'healthy' | 'at_risk' | 'breaching' | 'no_data';
}
interface TriageResponse { metrics: TriageRow[]; range: RangeKey; generatedAt: string; source: string }

const ROW_COLOR: Record<string, string> = {
  Critical: 'text-rose-500',
  High: 'text-red-400',
  Medium: 'text-amber-400',
  Low: 'text-blue-400',
  Informational: 'text-muted-foreground',
};

const STATUS_PILL: Record<TriageRow['status'], { label: string; cls: string }> = {
  healthy:    { label: 'Healthy',    cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
  at_risk:    { label: 'At Risk',    cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  breaching:  { label: 'Breaching',  cls: 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/40' },
  no_data:    { label: '-',          cls: 'bg-muted text-muted-foreground border-border' },
};

/**
 * Response-time performance — runs the Kloudsecure standard MTTT + MTTC
 * KQL queries against the customer's Sentinel workspace. The same component
 * is mounted on the analyst Overview tab AND the customer portal so all
 * three roles (admin, analyst, customer_viewer) see identical numbers driven
 * by a single endpoint.
 */
export function MttrWidget({
  customerId,
  defaultRange = '30d',
  showRangeSelector = true,
}: {
  customerId: string;
  defaultRange?: RangeKey;
  showRangeSelector?: boolean;
}) {
  const [range, setRange] = useState<RangeKey>(defaultRange);
  const [data, setData] = useState<TriageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch<TriageResponse>(
      `/sla/triage-metrics?customerId=${encodeURIComponent(customerId)}&range=${range}`,
    )
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load response-time metrics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, range]);

  const rangeLabel = RANGES.find((r) => r.key === range)?.label ?? range;

  return (
    <div className="bg-card rounded-xl shadow p-4 border border-border">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="text-sm font-bold text-foreground">
          Response-time performance · last {rangeLabel}
        </h3>
        <div className="flex items-center gap-2">
          {loading && <span className="text-[10px] text-muted-foreground">loading…</span>}
          {showRangeSelector && (
            <div className="inline-flex rounded-lg border border-border bg-muted/20 p-0.5">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRange(r.key)}
                  className={`px-2 py-0.5 text-[11px] font-semibold rounded-md transition-colors ${
                    range === r.key
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-pressed={range === r.key}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2">
        Mean Time to Triage (MTTT) and Mean Time to Close (MTTC) computed live
        from your Sentinel <code className="text-[10px] bg-muted/40 px-1 rounded">SecurityIncident</code> table.
      </p>
      {error && <div className="text-xs text-red-400 mb-2">{error}</div>}
      {data && (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left py-1" title="Incident severity">Severity</th>
              <th className="text-right" title="Number of incidents that received first triage in this window">Incidents</th>
              <th className="text-right" title="Mean Time to Triage. Average from CreatedTime to first non-New status or owner assignment.">
                <abbr title="Mean Time to Triage" className="no-underline">Mean Time to Triage</abbr>
              </th>
              <th className="text-right" title="Mean Time to Close. Average from CreatedTime to ClosedTime, closed incidents only.">
                <abbr title="Mean Time to Close" className="no-underline">Mean Time to Close</abbr>
              </th>
              <th className="text-right" title="Healthy / At Risk (≥80% of SLA budget) / Breaching (mean exceeded SLA)">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.metrics.map((m) => {
              const status = STATUS_PILL[m.status];
              return (
                <tr key={m.severity} className="border-t border-border/50">
                  <td className={`py-1.5 font-semibold ${ROW_COLOR[m.severity] ?? ''}`}>{m.severity}</td>
                  <td className="text-right">{m.triageCount}</td>
                  <td className="text-right tabular-nums">{m.mtttFormatted}</td>
                  <td className="text-right tabular-nums">{m.mttcFormatted}</td>
                  <td className="text-right">
                    <span
                      className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded border ${status.cls}`}
                      title={`SLA: triage ≤ ${m.ttaPolicyMinutes}m, close ≤ ${m.ttrPolicyMinutes}m`}
                    >
                      {status.label}
                    </span>
                  </td>
                </tr>
              );
            })}
            {data.metrics.every((m) => m.triageCount === 0 && m.closeCount === 0) && (
              <tr>
                <td colSpan={5} className="py-3 text-center text-muted-foreground italic">
                  No triaged or closed incidents in this window.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
